import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { clearCart } from '../store/cartSlice';
import { useStoreCurrency } from '../hooks/useStoreCurrency';
import { useStoreSettings } from '../hooks/useStoreSettings';

interface OrderItem {
  name: string;
  quantity: number;
  price: number;
  product_id: string;
}

interface OrderDetails {
  id: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  total_amount: number;
  items: string | OrderItem[]; // može biti JSON string ili već raščlanjen niz
  shipping_address: string;
  shipping_city: string;
  shipping_country: string;
  shipping_postal_code: string;
  created_at: string;
  status: string;
}

interface ShippingInfo {
  fullName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  country: string;
  postalCode: string;
}

const OrderConfirmation = () => {
  console.log("OrderConfirmation komponenta je ucitana");
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [orderDetails, setOrderDetails] = useState<OrderDetails | null>(null);
  const [shippingInfo, setShippingInfo] = useState<ShippingInfo | null>(null);
  const [parsedItems, setParsedItems] = useState<OrderItem[]>([]);
  // Inline stilovi ne podržavaju media query — pratimo širinu radi responsivnosti.
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.matchMedia('(max-width: 600px)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 600px)');
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    setIsMobile(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  const { formatPrice: formatCurrencyPrice } = useStoreCurrency();
  const { formatPrice } = useStoreSettings();
  
  // Funkcija za formatiranje cene
  const getFormattedPrice = (price: number) => {
    // Prvo pokušamo sa currency formatiranjem, pa onda sa standardnim
    return formatCurrencyPrice(price) || formatPrice(price) || `${price.toFixed(2)} RSD`;
  };
  
  useEffect(() => {
    console.log("OrderConfirmation useEffect se izvrsio");
    document.title = "Potvrda porudžbine";
    
    // Dobijamo order detalje iz location state-a
    if (location.state?.orderDetails) {
      console.log('OrderConfirmation - Order details:', location.state.orderDetails);
      setOrderDetails(location.state.orderDetails);
      setShippingInfo(location.state.shippingInfo || null);
      
      try {
        // Parsiranje JSON stringa u niz stavki porudžbine
        console.log('OrderConfirmation - Items:', location.state.orderDetails.items);
        let items = [];
        if (typeof location.state.orderDetails.items === 'string') {
          items = JSON.parse(location.state.orderDetails.items);
        } else if (Array.isArray(location.state.orderDetails.items)) {
          items = location.state.orderDetails.items;
        }
        console.log('OrderConfirmation - Parsed items:', items);
        setParsedItems(items);
      } catch (error) {
        console.error('Error parsing order items:', error);
        setParsedItems([]);
      }
    }
  }, [location]);

  // Povratak sa Stripe-a: state je izgubljen jer je bio redirect na tuđi
  // domen, pa se oslanjamo na query parametre. Korpu praznimo tek ovde —
  // kupac koji je odustao vraća se na punu korpu.
  const params = new URLSearchParams(location.search);
  const paidReturn = params.get('paid') === '1';
  const returnedOrderId = params.get('order');

  useEffect(() => {
    if (paidReturn) dispatch(clearCart());
  }, [paidReturn, dispatch]);

  // Funkcija za formatiranje datuma
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Stilovi za komponente
  const styles = {
    page: {
      maxWidth: '1000px',
      margin: '40px auto',
      padding: '0 20px',
      fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    card: {
      background: 'white',
      borderRadius: '12px',
      boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
      padding: isMobile ? '20px 16px' : '40px',
      opacity: 1
    },
    header: {
      textAlign: 'center' as const,
      marginBottom: '40px',
      paddingBottom: '30px',
      borderBottom: '1px solid #f0f0f0'
    },
    iconWrapper: {
      background: '#ebfbf3',
      width: '80px',
      height: '80px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 20px'
    },
    checkIcon: {
      color: '#4caf50',
      fontSize: '40px'
    },
    title: {
      color: '#232323',
      margin: '0 0 10px',
      fontSize: '28px'
    },
    subtitle: {
      color: '#666',
      margin: '0 0 20px',
      fontSize: '16px'
    },
    orderNumber: {
      backgroundColor: '#f8f9fa',
      padding: '15px',
      borderRadius: '8px',
      display: 'inline-block',
      margin: '0 auto'
    },
    orderNumberText: {
      margin: '5px 0',
      color: '#555',
      fontSize: '14px'
    },
    orderNumberValue: {
      fontWeight: 600,
      color: '#333'
    },
    orderInfo: {
      display: 'grid',
      gap: '30px'
    },
    gridContainer: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
      gap: isMobile ? '16px' : '30px'
    },
    section: {
      padding: isMobile ? '16px' : '25px',
      backgroundColor: '#f8f9fa',
      borderRadius: '10px',
      boxShadow: '0 2px 5px rgba(0,0,0,0.02)',
      transition: 'box-shadow 0.3s ease'
    },
    sectionTitle: {
      color: '#232323',
      margin: '0 0 20px',
      fontSize: '18px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      borderBottom: '1px solid #ebebeb',
      paddingBottom: '15px'
    },
    sectionText: {
      margin: '10px 0',
      color: '#444',
      lineHeight: '1.5'
    },
    statusBadge: {
      display: 'inline-block',
      padding: '5px 10px',
      backgroundColor: '#e3f2fd',
      color: '#1976d2',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: 600,
      textTransform: 'uppercase' as const
    },
    totalAmount: {
      fontWeight: 600,
      color: 'var(--accent-color)',
      fontSize: '17px'
    },
    productsSection: {
      padding: isMobile ? '16px' : '25px',
      backgroundColor: '#f8f9fa',
      borderRadius: '10px',
      boxShadow: '0 2px 5px rgba(0,0,0,0.02)'
    },
    productsList: {
      marginTop: '20px',
      borderRadius: '8px',
      overflow: 'hidden',
      background: 'white',
      boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
    },
    productsHeader: {
      display: isMobile ? 'none' : 'grid',
      gridTemplateColumns: '3fr 1fr 1fr 1fr',
      gap: '10px',
      padding: '15px 20px',
      background: '#f0f0f0',
      fontWeight: 600,
      fontSize: '14px',
      color: '#444'
    },
    product: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '3fr 1fr 1fr 1fr',
      gap: isMobile ? '4px' : '10px',
      padding: isMobile ? '12px 14px' : '15px 20px',
      alignItems: isMobile ? 'stretch' : 'center',
      borderBottom: '1px solid #f0f0f0'
    },
    productMobileRow: {
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: '13px',
      color: '#666'
    },
    productName: {
      fontWeight: 500,
      color: '#333'
    },
    productQuantity: {
      color: '#666',
      fontWeight: 500
    },
    productPrice: {
      fontWeight: 500,
      color: '#333'
    },
    productTotal: {
      fontWeight: 600,
      color: '#333'
    },
    orderSummary: {
      padding: '20px',
      background: '#fafafa',
      borderTop: '1px solid #f0f0f0'
    },
    summaryRow: {
      display: 'flex',
      justifyContent: 'space-between',
      margin: '10px 0',
      fontSize: '14px',
      color: '#555'
    },
    grandTotal: {
      fontWeight: 600,
      color: '#232323',
      fontSize: '18px',
      marginTop: '15px',
      paddingTop: '15px',
      borderTop: '1px solid #e6e6e6'
    },
    actions: {
      marginTop: '40px',
      textAlign: 'center' as const
    },
    message: {
      maxWidth: '600px',
      margin: '0 auto 30px',
      color: '#666',
      lineHeight: '1.6'
    },
    buttons: {
      display: 'flex',
      flexDirection: isMobile ? ('column' as const) : ('row' as const),
      justifyContent: 'center',
      gap: '15px'
    },
    button: {
      padding: '12px 20px',
      borderRadius: '5px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '14px',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      border: 'none'
    },
    printButton: {
      backgroundColor: '#f5f5f5',
      color: '#333'
    },
    continueButton: {
      backgroundColor: '#4caf50',
      color: 'white'
    }
  };

  // Za demo prikaz ili stvarne podatke (prikazujemo demo ako nema stvarnih podataka)
  const isDemo = !orderDetails;
  
  // Defaultni demo podaci ako nema stvarnih podataka
  const demoOrderData = {
    id: "DEMO-12345",
    date: new Date().toLocaleDateString(),
    status: "POTVRĐENO",
    total: "5000.00 RSD",
    customer: {
      name: "Petar Petrović",
      email: "petar@example.com",
      phone: "+381 60 123 4567"
    },
    shipping: {
      address: "Knez Mihailova 1",
      city: "Beograd",
      postalCode: "11000",
      country: "Srbija"
    },
    items: [
      { id: 1, name: "Demo Proizvod 1", quantity: 2, price: "1500.00 RSD", total: "3000.00 RSD" },
      { id: 2, name: "Demo Proizvod 2", quantity: 1, price: "2000.00 RSD", total: "2000.00 RSD" }
    ]
  };

  // Povratak sa Stripe-a nema detalje porudžbine u state-u (redirect je bio
  // na tuđi domen), a te podatke gost ne sme ni da čita iz baze. Zato kratka
  // potvrda umesto pune strane sa praznim poljima.
  if (!orderDetails && returnedOrderId) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.header}>
            <div style={styles.iconWrapper}>
              <div style={styles.checkIcon}>✓</div>
            </div>
            <h1 style={styles.title}>Uplata je primljena!</h1>
            <p style={styles.subtitle}>
              Hvala na kupovini. Potvrdu i detalje porudžbine šaljemo na vaš email.
            </p>
            <div style={styles.orderNumber}>
              <p style={styles.orderNumberText}>
                Broj porudžbine:{' '}
                <span style={styles.orderNumberValue}>{returnedOrderId}</span>
              </p>
            </div>
          </div>
          <div style={{ textAlign: 'center', padding: '10px 0 30px' }}>
            <Link to="/products" style={{ color: 'var(--accent-color)', fontWeight: 600 }}>
              Nastavi kupovinu →
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.iconWrapper}>
            <div style={styles.checkIcon}>✓</div>
          </div>
          <h1 style={styles.title}>Uspešna porudžbina!</h1>
          <p style={styles.subtitle}>Hvala na kupovini! Vaša porudžbina je potvrđena.</p>
          <div style={styles.orderNumber}>
            <p style={styles.orderNumberText}>Broj porudžbine: <span style={styles.orderNumberValue}>{orderDetails?.id || "Nije dostupno"}</span></p>
          </div>
        </div>

        <div style={styles.orderInfo}>
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>
              🕒 Detalji porudžbine
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr', gap: isMobile ? '12px' : '20px' }}>
              <div>
                <p style={styles.sectionText}><strong>Datum:</strong></p>
                <p style={styles.sectionText}>{orderDetails ? formatDate(orderDetails.created_at) : new Date().toLocaleDateString()}</p>
              </div>
              <div>
                <p style={styles.sectionText}><strong>Status:</strong></p>
                <p style={styles.sectionText}>
                  <span style={styles.statusBadge}>
                    {orderDetails?.status?.toUpperCase() || "POTVRĐENO"}
                  </span>
                </p>
              </div>
              <div>
                <p style={styles.sectionText}><strong>Način plaćanja:</strong></p>
                <p style={styles.sectionText}>Pouzećem</p>
              </div>
              <div>
                <p style={styles.sectionText}><strong>Ukupan iznos:</strong></p>
                <p style={styles.totalAmount}>
                  {orderDetails ? getFormattedPrice(orderDetails.total_amount) : "0.00 RSD"}
                </p>
              </div>
            </div>
          </div>

          <div style={styles.gridContainer}>
            <div style={styles.section}>
              <h2 style={styles.sectionTitle}>
                ℹ️ Informacije o kupcu
              </h2>
              <p style={styles.sectionText}><strong>Ime i prezime:</strong> {orderDetails?.customer_name || shippingInfo?.fullName || "Nije dostupno"}</p>
              <p style={styles.sectionText}><strong>Email:</strong> {orderDetails?.customer_email || shippingInfo?.email || "Nije dostupno"}</p>
              <p style={styles.sectionText}><strong>Telefon:</strong> {orderDetails?.customer_phone || shippingInfo?.phone || "Nije dostupno"}</p>
            </div>

            <div style={styles.section}>
              <h2 style={styles.sectionTitle}>
                📍 Adresa za dostavu
              </h2>
              <p style={styles.sectionText}>{orderDetails?.shipping_address || shippingInfo?.address || "Nije dostupno"}</p>
              <p style={styles.sectionText}>
                {orderDetails?.shipping_city || shippingInfo?.city || "Nije dostupno"}, 
                {orderDetails?.shipping_postal_code || shippingInfo?.postalCode || "Nije dostupno"}
              </p>
              <p style={styles.sectionText}>{orderDetails?.shipping_country || shippingInfo?.country || "Nije dostupno"}</p>
            </div>
          </div>

          <div style={styles.productsSection}>
            <h2 style={styles.sectionTitle}>
              🛍️ Kupljeni proizvodi
            </h2>
            <div style={styles.productsList}>
              <div style={styles.productsHeader}>
                <span>Proizvod</span>
                <span>Količina</span>
                <span>Cena</span>
                <span>Ukupno</span>
              </div>
              
              {isDemo ? (
                // Demo proizvodi
                demoOrderData.items.map((item, index) => (
                  <div key={index} style={styles.product}>
                    <span style={styles.productName}>{item.name}</span>
                    {isMobile ? (
                      <>
                        <div style={styles.productMobileRow}><span>Količina</span><span style={styles.productQuantity}>{item.quantity}</span></div>
                        <div style={styles.productMobileRow}><span>Cena</span><span style={styles.productPrice}>{item.price}</span></div>
                        <div style={styles.productMobileRow}><span>Ukupno</span><span style={styles.productTotal}>{item.total}</span></div>
                      </>
                    ) : (
                      <>
                        <span style={styles.productQuantity}>{item.quantity}</span>
                        <span style={styles.productPrice}>{item.price}</span>
                        <span style={styles.productTotal}>{item.total}</span>
                      </>
                    )}
                  </div>
                ))
              ) : (
                // Stvarni proizvodi
                parsedItems.map((item, index) => (
                  <div key={index} style={styles.product}>
                    <span style={styles.productName}>{item.name}</span>
                    {isMobile ? (
                      <>
                        <div style={styles.productMobileRow}><span>Količina</span><span style={styles.productQuantity}>{item.quantity}</span></div>
                        <div style={styles.productMobileRow}><span>Cena</span><span style={styles.productPrice}>{getFormattedPrice(item.price)}</span></div>
                        <div style={styles.productMobileRow}><span>Ukupno</span><span style={styles.productTotal}>{getFormattedPrice(item.price * item.quantity)}</span></div>
                      </>
                    ) : (
                      <>
                        <span style={styles.productQuantity}>{item.quantity}</span>
                        <span style={styles.productPrice}>{getFormattedPrice(item.price)}</span>
                        <span style={styles.productTotal}>{getFormattedPrice(item.price * item.quantity)}</span>
                      </>
                    )}
                  </div>
                ))
              )}
              
              <div style={styles.orderSummary}>
                <div style={styles.summaryRow}>
                  <span>Ukupno proizvodi:</span>
                  <span>{orderDetails ? getFormattedPrice(orderDetails.total_amount) : demoOrderData.total}</span>
                </div>
                <div style={styles.summaryRow}>
                  <span>Dostava:</span>
                  <span>Besplatna</span>
                </div>
                <div style={{...styles.summaryRow, ...styles.grandTotal}}>
                  <span>Ukupno za plaćanje:</span>
                  <span>{orderDetails ? getFormattedPrice(orderDetails.total_amount) : demoOrderData.total}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={styles.actions}>
          <div style={styles.message}>
            <p>
              Kopija potvrde je poslata na Vašu email adresu. 
              Ako imate bilo kakva pitanja, kontaktirajte našu korisničku podršku.
            </p>
          </div>
          <div style={styles.buttons}>
            <button onClick={() => window.print()} style={{...styles.button, ...styles.printButton}}>
              🖨️ Štampaj potvrdu
            </button>
            <button onClick={() => navigate('/')} style={{...styles.button, ...styles.continueButton}}>
              🏠 Nastavi kupovinu
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmation; 